import React, { useEffect, useState } from 'react';
import { XIcon, CameraIcon, LockIcon, KeyIcon, ChevronDownIcon } from 'lucide-react';
import { useUser, UserRole } from '../contexts/UserContext';
interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function EditProfileModal({
  isOpen,
  onClose
}: EditProfileModalProps) {
  const {
    role: globalRole,
    setRole: setGlobalRole,
    name: globalName,
    setName: setGlobalName,
    email: globalEmail,
    setEmail: setGlobalEmail
  } = useUser();
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [name, setName] = useState(globalName);
  const [email, setEmail] = useState(globalEmail);
  const [role, setRole] = useState<UserRole>(globalRole);
  const [country, setCountry] = useState('México');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowPasswordSection(false);
      setName(globalName);
      setEmail(globalEmail);
      setRole(globalRole);
      setCountry('México');
    }
  }, [isOpen, globalName, globalEmail, globalRole]);
  if (!isOpen) return null;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia'];
  const roles = [{
    value: 'authenticated-public-user',
    label: 'Authenticated Public User'
  }, {
    value: 'customer',
    label: 'Customer'
  }, {
    value: 'dealer',
    label: 'Dealer'
  }, {
    value: 'sales-manager',
    label: 'Sales Manager'
  }, {
    value: 'operations-manager',
    label: 'Operations Manager'
  }, {
    value: 'manager-business-unit',
    label: 'Manager Business Unit'
  }, {
    value: 'ceo',
    label: 'CEO'
  }, {
    value: 'logistic',
    label: 'Logistic'
  }, {
    value: 'marketing',
    label: 'Marketing'
  }, {
    value: 'financial-manager',
    label: 'Financial Manager'
  }, {
    value: 'system-admin',
    label: 'System Admin'
  }, {
    value: 'dealer-admin',
    label: 'Dealer Admin'
  }, {
    value: 'region-admin',
    label: 'Region Admin'
  }];
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleBackdropClick} data-id="element-3263">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-lg my-8 relative flex flex-col max-h-[90vh]" data-id="element-3264">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0" data-id="element-3265">
          <h2 className="text-white font-semibold text-lg" data-id="element-3266">Edit Profile</h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-3267">
            <XIcon size={20} data-id="element-3268" />
          </button>
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar" data-id="element-3269">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8" data-id="element-3270">
            <div className="relative" data-id="element-3271">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-4 ring-[#0f1d3a] shadow-lg" data-id="element-3272">
                <span className="text-2xl font-bold text-white" data-id="element-3273">DA</span>
              </div>
              <button className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-500 text-white rounded-full p-1.5 shadow-lg border-2 border-[#0f1d3a] transition-colors" title="Change photo" data-id="element-3274">
                <CameraIcon size={14} data-id="element-3275" />
              </button>
            </div>
            <span className="text-blue-400 text-xs mt-2 font-medium cursor-pointer hover:text-blue-300 transition-colors" data-id="element-3276">
              Change photo
            </span>
            <span className="inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-300 border border-teal-500/30" data-id="element-3277">
              Dealer
            </span>
          </div>

          {/* Form Fields */}
          <div className="space-y-5" data-id="element-3278">
            <div data-id="element-3279">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3280">
                Full name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Your name" data-id="element-3281" />
            </div>

            <div data-id="element-3282">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3283">
                Email
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="your@email.com" data-id="element-3284" />
            </div>

            <div data-id="element-3285">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3286">
                Role
              </label>
              <div className="relative" data-id="element-3287">
                <select value={role} onChange={e => setRole(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3288">
                  {roles.map(r => <option key={r.value} value={r.value} className="bg-slate-800 text-white" data-id="element-3289">
                      {r.label}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3290">
                  <ChevronDownIcon size={16} data-id="element-3291" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3292">
              <div data-id="element-3293">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3294">
                  Country
                </label>
                <div className="relative" data-id="element-3295">
                  <select value={country} onChange={e => setCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3296">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3297">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3298">
                    <ChevronDownIcon size={16} data-id="element-3299" />
                  </div>
                </div>
              </div>

              <div data-id="element-3300">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3301">
                  Region
                </label>
                <div className="relative" data-id="element-3302">
                  <input type="text" value="Aisco America" readOnly className="w-full bg-white/5 border border-white/5 rounded-lg pl-10 pr-4 py-2.5 text-blue-300/70 cursor-not-allowed focus:outline-none" data-id="element-3303" />
                  <LockIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400/50" data-id="element-3304" />
                </div>
              </div>
            </div>

            {/* Change Password Section */}
            <div className="pt-4 border-t border-white/10 mt-6" data-id="element-3305">
              <button type="button" onClick={() => setShowPasswordSection(!showPasswordSection)} className="flex items-center justify-between w-full text-left group" data-id="element-3306">
                <div className="flex items-center text-blue-400 group-hover:text-blue-300 transition-colors" data-id="element-3307">
                  <KeyIcon size={18} className="mr-2" data-id="element-3308" />
                  <span className="font-medium text-sm" data-id="element-3309">Change password</span>
                </div>
                <ChevronDownIcon size={18} className={`text-blue-400 group-hover:text-blue-300 transition-transform duration-200 ${showPasswordSection ? 'rotate-180' : ''}`} data-id="element-3310" />
              </button>

              {showPasswordSection && <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-3311">
                  <div data-id="element-3312">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3313">
                      New password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3314" />
                  </div>
                  <div data-id="element-3315">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3316">
                      Confirm password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3317" />
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end space-x-3 flex-shrink-0 bg-black/20 rounded-b-2xl" data-id="element-3318">
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors" data-id="element-3319">
            Cancel
          </button>
          <button onClick={() => {
          setGlobalRole(role);
          setGlobalName(name);
          setGlobalEmail(email);
          onClose();
        }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-3320">
            Save changes
          </button>
        </div>
      </div>
    </div>;
}